// Client-side Ed25519 PEM utilities for the dashboard registered-key
// flow (PR 15-B). These mirror the server-side canonical form so a key
// generated or validated here is byte-identical to what
// src/lib/keys.ts (parseEd25519PublicKeyPem / canonicalize) produces and
// what src/lib/hash.ts (agentKeyFingerprint) hashes. The broker compares
// canonical forms, so matching the canonical PEM exactly means a key
// registered through the dashboard is accepted on the API path too.
//
// Pure and isomorphic: uses only btoa/atob, TextEncoder, and
// crypto.subtle.digest, which are available in every modern browser and
// in Node (so these functions are unit-testable under node:test without
// a DOM). Keypair GENERATION lives in keygen.ts, which is the only place
// that pulls in @noble/curves; this file stays dependency-free.

// Fixed ASN.1 prefixes for Ed25519. The SubjectPublicKeyInfo (SPKI)
// prefix is 12 bytes and precedes the 32-byte public key (44-byte DER
// total). The PKCS8 prefix is 16 bytes and precedes the 32-byte private
// key seed (48-byte DER total). Both are constant for Ed25519; verified
// against Node's crypto export in tests.
const SPKI_ED25519_PREFIX = Uint8Array.from([
  0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00
]);
const PKCS8_ED25519_PREFIX = Uint8Array.from([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20
]);

const PUBLIC_KEY_LABEL = "PUBLIC KEY";
const PRIVATE_KEY_LABEL = "PRIVATE KEY";

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

// Wrap DER bytes in a PEM block matching Node's canonical output: LF line
// endings, base64 wrapped at 64 chars, and a trailing newline.
function toPem(der: Uint8Array, label: string): string {
  const base64 = bytesToBase64(der);
  const lines = base64.match(/.{1,64}/g) ?? [base64];
  return `-----BEGIN ${label}-----\n${lines.join("\n")}\n-----END ${label}-----\n`;
}

// Encode a 32-byte Ed25519 public key as a canonical SPKI PEM. The
// output is byte-identical to Node's
// publicKey.export({ type: "spki", format: "pem" }).
export function encodeEd25519SpkiPem(publicKeyBytes: Uint8Array): string {
  if (publicKeyBytes.length !== 32) {
    throw new Error(`Ed25519 public key must be 32 bytes, got ${publicKeyBytes.length}.`);
  }
  const der = new Uint8Array(SPKI_ED25519_PREFIX.length + 32);
  der.set(SPKI_ED25519_PREFIX, 0);
  der.set(publicKeyBytes, SPKI_ED25519_PREFIX.length);
  return toPem(der, PUBLIC_KEY_LABEL);
}

// Encode a 32-byte Ed25519 private key seed as a canonical PKCS8 PEM,
// the format the SDK consumes (privateKey.export pkcs8). Byte-identical
// to Node's export for the same seed.
export function encodeEd25519Pkcs8Pem(privateKeyBytes: Uint8Array): string {
  if (privateKeyBytes.length !== 32) {
    throw new Error(`Ed25519 private key seed must be 32 bytes, got ${privateKeyBytes.length}.`);
  }
  const der = new Uint8Array(PKCS8_ED25519_PREFIX.length + 32);
  der.set(PKCS8_ED25519_PREFIX, 0);
  der.set(privateKeyBytes, PKCS8_ED25519_PREFIX.length);
  return toPem(der, PRIVATE_KEY_LABEL);
}

export type ParsedPublicKey =
  | { ok: true; bytes: Uint8Array; pem: string }
  | { ok: false; reason: string };

// Validate a pasted Ed25519 public-key PEM client-side, mirroring the
// server's parseEd25519PublicKeyPem. On success returns the raw 32-byte
// key and the canonical SPKI PEM (re-emitted, so downstream code can hash
// the canonical form regardless of the customer's formatting).
export function parseEd25519PublicKeyPem(pem: string): ParsedPublicKey {
  const match = pem.trim().match(/-----BEGIN PUBLIC KEY-----([\s\S]*?)-----END PUBLIC KEY-----/);
  if (!match) {
    return {
      ok: false,
      reason:
        'Expected a PEM block delimited by "-----BEGIN PUBLIC KEY-----" and "-----END PUBLIC KEY-----".'
    };
  }
  const base64 = match[1].replace(/\s+/g, "");
  let der: Uint8Array;
  try {
    der = base64ToBytes(base64);
  } catch {
    return { ok: false, reason: "The PEM body is not valid base64." };
  }
  if (der.length !== SPKI_ED25519_PREFIX.length + 32) {
    return {
      ok: false,
      reason: `Expected a 44-byte Ed25519 SPKI key, got ${der.length} bytes. This may be a different key type (RSA, P-256).`
    };
  }
  for (let i = 0; i < SPKI_ED25519_PREFIX.length; i++) {
    if (der[i] !== SPKI_ED25519_PREFIX[i]) {
      return {
        ok: false,
        reason:
          "This is not an Ed25519 public key (the SPKI algorithm identifier does not match Ed25519)."
      };
    }
  }
  const bytes = der.slice(SPKI_ED25519_PREFIX.length);
  return { ok: true, bytes, pem: encodeEd25519SpkiPem(bytes) };
}

// Compute the agent key fingerprint client-side. Identical algorithm to
// src/lib/hash.ts agentKeyFingerprint: "sha256:" followed by the
// lowercase hex SHA-256 of the UTF-8 bytes of the CANONICAL SPKI PEM.
// Pass the canonical PEM (from encodeEd25519SpkiPem or the .pem field of
// a successful parse) so the result matches what the verifier reports.
export async function fingerprint(canonicalPem: string): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalPem);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `sha256:${hex}`;
}
